<template>
    <div class="ai-config">
        <div class="page-header">
            <h2 class="page-title">接口配置</h2>
            <p class="page-desc">配置AI绘图/视频接口和AI对话接口</p>
        </div>

        <div class="config-container">
            <!-- AI绘图配置 -->
            <el-card class="config-card" shadow="hover">
                <template #header>
                    <div class="card-header-title">
                        <el-icon>
                            <Picture />
                        </el-icon>
                        <span>AI绘图/视频接口</span>
                    </div>
                </template>

                <el-form :model="drawForm" ref="drawFormRef" label-width="140px">
                    <el-form-item label="API密钥">
                        <el-input v-model="drawForm.ai_draw_key" type="password" placeholder="请输入AI绘图/视频接口密钥"
                            show-password />
                        <div class="form-tip">用于AI生成图片、视频等功能</div>
                    </el-form-item>

                    <el-form-item class="save-btn-item">
                        <el-button type="primary" @click="saveDrawConfig" :loading="submitting.draw" size="default"
                            class="save-btn">
                            <el-icon>
                                <Check />
                            </el-icon>保存AI绘图配置
                        </el-button>
                    </el-form-item>
                </el-form>
            </el-card>

            <!-- AI对话接口配置 -->
            <el-card class="config-card" shadow="hover">
                <template #header>
                    <div class="card-header-title">
                        <el-icon>
                            <ChatDotRound />
                        </el-icon>
                        <span>AI对话接口</span>
                    </div>
                </template>

                <el-form :model="aiForm" ref="aiFormRef" label-width="140px">
                    <el-form-item label="AI服务商" required>
                        <el-select v-model="aiForm.ai_provider" placeholder="请选择AI服务商" style="width: 300px"
                            @change="onProviderChange">
                            <el-option label="DeepSeek" value="deepseek" />
                            <el-option label="通义千问" value="tongyi" />
                            <el-option label="OpenAI" value="openai" />
                            <el-option label="自定义" value="custom" />
                        </el-select>
                    </el-form-item>

                    <el-form-item label="接口地址" v-if="aiForm.ai_provider === 'custom'">
                        <el-input v-model="aiForm.ai_api_url"
                            placeholder="请输入API接口地址，如：https://api.openai.com/v1/chat/completions" />
                        <div class="form-tip">自定义接口需要符合OpenAI格式</div>
                    </el-form-item>

                    <el-form-item label="模型名称">
                        <el-input v-model="aiForm.ai_model" placeholder="请输入模型名称">
                            <template #prepend v-if="aiForm.ai_provider === 'deepseek'">deepseek-</template>
                            <template #prepend v-else-if="aiForm.ai_provider === 'tongyi'">qwen-</template>
                            <template #prepend v-else-if="aiForm.ai_provider === 'openai'">gpt-</template>
                        </el-input>
                        <div class="form-tip" v-if="aiForm.ai_provider === 'deepseek'">可选：chat, coder, reasoner</div>
                        <div class="form-tip" v-else-if="aiForm.ai_provider === 'tongyi'">可选：plus, turbo, max</div>
                        <div class="form-tip" v-else-if="aiForm.ai_provider === 'openai'">可选：3.5-turbo, 4, 4-turbo</div>
                    </el-form-item>

                    <el-form-item label="API密钥">
                        <el-input v-model="aiForm.ai_api_key" type="password" placeholder="请输入API密钥" show-password />
                    </el-form-item>

                    <el-row :gutter="20">
                        <el-col :span="12">
                            <el-form-item label="最大令牌数">
                                <el-input-number v-model="aiForm.ai_max_tokens" :min="1" :max="32000" :step="512"
                                    style="width: 100%" />
                                <div class="form-tip">控制生成内容的最大长度</div>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="温度参数">
                                <el-slider v-model="aiForm.ai_temperature" :min="0" :max="2" :step="0.01"
                                    :marks="{ 0: '精确', 0.5: '平衡', 1: '创意', 2: '随机' }" />
                                    <div class="form-tip" style="margin-top: 32px;">值越高，输出越随机创意</div>
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <el-form-item class="save-btn-item">
                        <el-button type="primary" @click="saveAiConfig" :loading="submitting.ai" size="default"
                            class="save-btn">
                            <el-icon>
                                <Check />
                            </el-icon>保存AI对话配置
                        </el-button>
                    </el-form-item>
                </el-form>
            </el-card>
        </div>
    </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { Picture, ChatDotRound, Check } from '@element-plus/icons-vue'

export default {
    name: 'AiConfig',
    components: { Picture, ChatDotRound, Check },
    setup() {
        const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
        const storeId = ref(userInfo.id || 0)
        const agentId = ref(userInfo.agent_id || 0)

        const drawFormRef = ref(null)
        const aiFormRef = ref(null)
        const submitting = reactive({ draw: false, ai: false })

        const drawForm = reactive({
            ai_draw_key: ''
        })

        const aiForm = reactive({
            ai_provider: 'deepseek',
            ai_api_url: '',
            ai_model: 'chat',
            ai_api_key: '',
            ai_max_tokens: 2048,
            ai_temperature: 0.8
        })

        const fetchConfig = async () => {
            const loading = ElLoading.service({ text: '加载中...' })
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/ai_api.php`, {
                    params: { store_id: storeId.value, agent_id: agentId.value }
                })
                if (res.data.code === 200 && res.data.data) {
                    drawForm.ai_draw_key = res.data.data.ai_draw_key || ''
                    aiForm.ai_provider = res.data.data.ai_provider || 'deepseek'
                    aiForm.ai_api_url = res.data.data.ai_api_url || ''
                    aiForm.ai_model = res.data.data.ai_model || 'chat'
                    aiForm.ai_api_key = res.data.data.ai_api_key || ''
                    aiForm.ai_max_tokens = res.data.data.ai_max_tokens || 2048
                    aiForm.ai_temperature = parseFloat(res.data.data.ai_temperature) || 0.8
                }
            } catch (err) {
                console.error(err)
                ElMessage.error('获取配置失败')
            } finally {
                loading.close()
            }
        }

        const onProviderChange = () => {
            // 根据服务商设置默认模型
            const defaults = {
                deepseek: 'chat',
                tongyi: 'plus',
                openai: '3.5-turbo',
                custom: ''
            }
            aiForm.ai_model = defaults[aiForm.ai_provider] || ''

            // 设置默认接口地址
            const apiUrls = {
                deepseek: 'https://api.deepseek.com/v1/chat/completions',
                tongyi: 'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions',
                openai: 'https://api.openai.com/v1/chat/completions',
                custom: ''
            }
            if (aiForm.ai_provider !== 'custom') {
                aiForm.ai_api_url = apiUrls[aiForm.ai_provider]
            }
        }

        const saveDrawConfig = async () => {
            submitting.draw = true
            const loading = ElLoading.service({ text: '保存中...' })

            try {
                const payload = {
                    store_id: storeId.value,
                    agent_id: agentId.value,
                    ai_draw_key: drawForm.ai_draw_key,
                    ai_allow_parent_draw: 1,
                    ai_provider: aiForm.ai_provider,
                    ai_api_url: aiForm.ai_api_url,
                    ai_model: aiForm.ai_model,
                    ai_api_key: aiForm.ai_api_key,
                    ai_max_tokens: aiForm.ai_max_tokens,
                    ai_temperature: aiForm.ai_temperature,
                    ai_allow_parent: 1
                }
                const res = await axios.post(`${config.BASE_URL}/shop/api/ai_api.php`, payload)

                if (res.data.code === 200) {
                    ElMessage.success('保存成功')
                } else {
                    ElMessage.error(res.data.message || '保存失败')
                }
            } catch (err) {
                ElMessage.error('保存失败')
            } finally {
                submitting.draw = false
                loading.close()
            }
        }

        const saveAiConfig = async () => {
            submitting.ai = true
            const loading = ElLoading.service({ text: '保存中...' })

            try {
                const payload = {
                    store_id: storeId.value,
                    agent_id: agentId.value,
                    ai_draw_key: drawForm.ai_draw_key,
                    ai_allow_parent_draw: 1,
                    ai_provider: aiForm.ai_provider,
                    ai_api_url: aiForm.ai_api_url,
                    ai_model: aiForm.ai_model,
                    ai_api_key: aiForm.ai_api_key,
                    ai_max_tokens: aiForm.ai_max_tokens,
                    ai_temperature: aiForm.ai_temperature,
                    ai_allow_parent: 1
                }
                const res = await axios.post(`${config.BASE_URL}/shop/api/ai_api.php`, payload)

                if (res.data.code === 200) {
                    ElMessage.success('保存成功')
                } else {
                    ElMessage.error(res.data.message || '保存失败')
                }
            } catch (err) {
                ElMessage.error('保存失败')
            } finally {
                submitting.ai = false
                loading.close()
            }
        }

        onMounted(() => {
            fetchConfig()
        })

        return {
            drawForm,
            aiForm,
            drawFormRef,
            aiFormRef,
            submitting,
            saveDrawConfig,
            saveAiConfig,
            onProviderChange
        }
    }
}
</script>

<style scoped lang="scss">
.ai-config {
    .page-header {
        margin-bottom: 24px;

        .page-title {
            font-size: 24px;
            font-weight: 600;
            color: #1a1a2e;
            margin-bottom: 4px;
        }

        .page-desc {
            font-size: 14px;
            color: #8c8c8c;
        }
    }

    .config-container {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .config-card {
        border-radius: 16px;
        overflow: hidden;
    }

    .card-header-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 500;
    }

    .form-tip {
        font-size: 12px;
        color: #999;
        margin-top: 4px;
        line-height: 1.4;
    }

    .save-btn-item {
        margin-top: 16px;
    }

    .save-btn {
        padding: 8px 24px;
        font-size: 14px;
        font-weight: 500;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        border: none !important;
    }
}
</style>